import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactLenis } from 'lenis/react';
import { useTransform, motion, useScroll } from 'motion/react';
import SplitText from './SplitText';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { projects, getProjectTags } from '../data/projects';
import CurvedLoop from './CurvedLoop';

gsap.registerPlugin(ScrollTrigger);

const SelectedWorksSection = () => {
  const navigate = useNavigate();
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ['start start', 'end end'],
  });

  const parseProjectDate = (project) => {
    if (project?.date) {
      const parsedDate = Date.parse(`${project.date} 1`);
      if (!Number.isNaN(parsedDate)) return parsedDate;
    }

    const parsedYear = parseInt(project?.year, 10);
    if (!Number.isNaN(parsedYear)) {
      return Date.parse(`${parsedYear}-01-01`);
    }

    return 0;
  };

  const workItems = [...projects]
    .sort((a, b) => parseProjectDate(b) - parseProjectDate(a))
    .slice(0, 4);

  const lineRef = useRef(null);
  const latestLabelRef = useRef(null);

  useEffect(() => {
    if (lineRef.current) {
      gsap.fromTo(
        lineRef.current,
        { scaleX: 0, transformOrigin: 'left center' },
        {
          scaleX: 1,
          duration: 1.4,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: lineRef.current,
            start: 'top 80%',
            once: true
          }
        }
      );
    }
    if (latestLabelRef.current) {
      gsap.fromTo(
        latestLabelRef.current,
        { opacity: 0, y: 16 },
        {
          opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
          scrollTrigger: {
            trigger: latestLabelRef.current,
            start: 'top 85%',
            once: true
          }
        }
      );
    }
  }, []);

  return (
    <ReactLenis root>
      <div className="relative" ref={container}>
        {/* Logo Loop Banner - Above Selected Works */}
        <div
          className="w-full h-[71px] flex items-center overflow-hidden relative z-25"
          style={{ background: 'unset', backgroundColor: 'unset' }}
        >
          <CurvedLoop
            marqueeText="Be ✦ Creative ✦ With ✦ Everything ✦"
            curveAmount={0}
            interactive={false}
            className="font-space font-bold uppercase"
          />
        </div>

        {/* Header Section */}
        <section className="text-white w-full bg-[#262624] py-16 sm:py-20 lg:h-[70vh]">
          <div className="max-w-[1310px] mx-auto px-6 sm:px-10 lg:px-16">
            <div className="flex flex-col  items-start justify-start">
              <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between w-full gap-8">
                <div className="font-anton text-[18vw] sm:text-[140px] lg:text-[200px] text-licht leading-[0.9] tracking-[-4px] sm:tracking-[-6px] lg:tracking-[-8px] w-full lg:w-[761px]">
                  <SplitText
                    text="SELECTED"
                    className="mb-0"
                    splitType="words"
                    delay={250}
                    duration={0.9}
                    ease="power3.out"
                    from={{ opacity: 0, y: 40 }}
                    to={{ opacity: 1, y: 0 }}
                    threshold={0}
                    rootMargin={"+100px"}
                  />
                  <SplitText
                    text="WORKS"
                    splitType="words"
                    delay={250}
                    duration={0.9}
                    ease="power3.out"
                    from={{ opacity: 0, y: 40 }}
                    to={{ opacity: 1, y: 0 }}
                    threshold={0}
                    rootMargin={"+100px"}
                  />
                </div>

                <div className="font-space font-normal text-[15px] sm:text-[17px] lg:text-[20px] text-licht text-left lg:text-right w-full lg:w-[638px]">
                  <SplitText
                    text={"Recent client launches and personal experiments that prove you can trust a young studio to land bold ideas, move fast, and still sweat every micro-interaction."}
                    className="leading-[1.35] sm:leading-[1.2] lg:leading-[0.907]"
                    splitType="lines"
                    delay={200}
                    duration={0.9}
                    ease="power3.out"
                    from={{ opacity: 0, y: 20 }}
                    to={{ opacity: 1, y: 0 }}
                    threshold={0}
                    rootMargin={"+100px"}
                  />
                </div>
              </div>

              <div ref={lineRef} className="w-full h-px bg-licht opacity-60 mt-2 sm:mt-3"></div>

              <div className="font-bricolage font-medium text-[22px] sm:text-[26px] lg:text-[32px] text-white w-full">
                <p className="leading-[1.05] sm:leading-[0.95] mt-10">LATEST WORK</p>
              </div>
            </div>
          </div>
        </section>

        {/* Stacking Cards Section */}
        <section className="text-white w-full bg-[#262624]">
          <div
            className="relative w-full"
            style={{ height: `${Math.max(workItems.length, 1) * 100}vh` }}
          >
            {workItems.map((item, i) => {
              const targetScale = 1 - (workItems.length - i) * 0.05;
              return (
                <WorkCard
                  key={`work_${i}`}
                  i={i}
                  item={item}
                  progress={scrollYProgress}
                  range={[i * 0.25, 1]}
                  targetScale={targetScale}
                  navigate={navigate}
                />
              );
            })}
          </div>
        </section>

        {/* View all work CTA (Fase 2) */}
        <section className="bg-[#262624] text-licht pb-24 sm:pb-28 lg:pb-32">
          <div className="max-w-[1310px] mx-auto px-6 sm:px-10 lg:px-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="font-bricolage font-medium text-[22px] sm:text-[26px] lg:text-[32px] text-licht/80 leading-[1.1] max-w-[640px]">
              This is just the latest. The full archive spans design, motion, 3D and branding.
            </p>
            <button
              type="button"
              onClick={() => navigate('/works')}
              className="group inline-flex items-center gap-3 sm:gap-4 font-bricolage font-extrabold text-licht text-[20px] sm:text-[26px] lg:text-[30px] uppercase tracking-tight hover:text-[#f97b22] transition-colors duration-300 flex-shrink-0"
            >
              <span>View all work</span>
              <span className="w-11 h-11 sm:w-14 sm:h-14 rounded-full border-2 border-licht group-hover:bg-licht group-hover:text-mainkleur flex items-center justify-center transition-all duration-300 group-hover:rotate-[-45deg]">
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </button>
          </div>
        </section>
      </div>
    </ReactLenis>
  );
};

// WorkCard component for stacking animation
const WorkCard = ({ i, item, progress, range, targetScale, navigate }) => {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ['start end', 'start start'],
  });

  const imageScale = useTransform(scrollYProgress, [0, 1], [2, 1]);
  const scale = useTransform(progress, range, [1, targetScale]);

  const handleClick = () => {
    navigate(`/project/${item.id}`);
  };

  return (
    <div
      ref={container}
      className="h-screen flex items-center justify-center sticky top-0"
    >
      <motion.div
        style={{
          scale,
          top: `calc(-5vh + ${i * 25}px)`,
          backgroundColor: item.color,
          boxShadow: `0 40px 80px -32px ${item.color}, 0 0 0 1px ${item.color}`,
        }}
        className="flex flex-col relative -top-[25%] h-auto lg:h-[582px] w-[90vw] max-w-[946px] rounded-[20px] p-4 sm:p-5 lg:p-6 origin-top cursor-pointer hover:scale-[1.02] transition-transform"
        onClick={handleClick}
      >
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-5 h-full">
          <div className="flex flex-col justify-between w-full lg:w-[318px]">
            <div className="flex flex-col gap-3 lg:gap-4 text-white">
              <div className="font-bricolage font-normal text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.96px]">
                <p className="whitespace-pre">{`${item.year}     |     ${item.readTime}`}</p>
              </div>
              <div className="font-bricolage font-semibold text-[32px] sm:text-[36px] lg:text-[40px] tracking-[-1.6px]">
                <p className="leading-none">{item.title}</p>
              </div>

              {/* Sector + discipline labels (Fase 5) — frame the card as proof
                  of range, not just a thumbnail. */}
              <div className="flex flex-wrap gap-2 mt-1">
                {item.sector && (
                  <span className="font-space text-[11px] sm:text-xs uppercase tracking-[0.12em] text-white/90 border border-white/30 rounded-full px-2.5 py-1">
                    {item.sector.trim()}
                  </span>
                )}
                {getProjectTags(item).slice(0, 2).map((label) => (
                  <span
                    key={label}
                    className="font-space text-[11px] sm:text-xs uppercase tracking-[0.12em] text-mainkleur bg-white/85 rounded-full px-2.5 py-1"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center mt-4 lg:mt-0">
              <div className="flex items-center gap-3 text-white cursor-pointer hover:gap-5 transition-all duration-300">
                <span className="text-sm sm:text-base font-bricolage font-medium whitespace-nowrap">View This Work</span>
                <svg width="71" height="16" viewBox="0 0 71 16" fill="none" className="flex-shrink-0">
                  <path d="M0.5 8H70.5M70.5 8L63 0.5M70.5 8L63 15.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white h-[280px] sm:h-[350px] lg:h-[503px] rounded-[20px] flex-1">
            <div className="w-full h-full bg-gray-100 rounded-[20px] flex items-center justify-center relative overflow-hidden">
              <motion.div
                className="w-full h-full"
                style={{ scale: imageScale }}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SelectedWorksSection;
